import { createContext, useCallback, useContext, useEffect, useId, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const DirtyContext = createContext(null);

/**
 * Tracks “unsaved changes” across the admin portal. BrowserRouter has no route blocker,
 * so every in-shell navigation (sidebar, topbar, palette) goes through `guard()`,
 * and a native beforeunload prompt covers reload/close.
 */
export const DirtyProvider = ({ children }) => {
  const [dirtyKeys, setDirtyKeys] = useState({});
  const [pending, setPending] = useState(null);
  const isDirty = Object.values(dirtyKeys).some(Boolean);

  const setDirty = useCallback((key, value) => {
    setDirtyKeys((d) => (!!d[key] === !!value ? d : { ...d, [key]: !!value }));
  }, []);

  const guard = useCallback((action) => {
    if (isDirty) setPending(() => action);
    else action();
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const value = useMemo(() => ({ isDirty, setDirty, guard }), [isDirty, setDirty, guard]);

  return (
    <DirtyContext.Provider value={value}>
      {children}
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent className="bg-card" data-testid="admin-unsaved-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Du har ulagra endringar</AlertDialogTitle>
            <AlertDialogDescription className="text-base leading-relaxed">Går du vidare no, vert endringane borte. Vil du forkaste dei?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-unsaved-stay">Hald fram her</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { const run = pending; setDirtyKeys({}); setPending(null); run?.(); }} data-testid="admin-unsaved-discard">Forkast endringar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DirtyContext.Provider>
  );
};

/** Register a dirty flag for the current component. Cleared automatically on unmount. */
export const useDirtyGuard = (dirty, key) => {
  const ctx = useContext(DirtyContext);
  const autoKey = useId();
  const k = key || autoKey;
  const setDirty = ctx?.setDirty;
  useEffect(() => {
    if (!setDirty) return undefined;
    setDirty(k, !!dirty);
    return () => setDirty(k, false);
  }, [dirty, k, setDirty]);
};

/** Navigate, but ask first when something is unsaved. */
export const useGuardedNavigate = () => {
  const navigate = useNavigate();
  const ctx = useContext(DirtyContext);
  return useCallback((to, opts) => {
    const go = () => navigate(to, opts);
    if (ctx) ctx.guard(go);
    else go();
  }, [navigate, ctx]);
};

export const useDirtyState = () => useContext(DirtyContext)?.isDirty || false;
